/**
 * OASIS Form Component - MVP Implementation
 * Dynamic form for creating and editing OASIS assessments
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Calculator, AlertCircle, Info, ChevronDown, ChevronRight, Check, AlertTriangle
} from 'lucide-react';
import PrimaryButton from '../../common/PrimaryButton';
import PrimaryButtonLight from '../../common/PrimaryButtonLight';
import Dialog from '../../common/Dialog';
import { FormButtonBar } from '../../common/FormButtons';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import { OasisService } from '../../../services/patientmr/oasis.service';
import ICDSelect from '../../common/ICDSelect';
import { AssessmentStatus } from '../../../services/patientmr/assessments.service';
import {
  OasisFormData,
  OasisResponses,
  OasisValidationResult,
  OasisType,
  HippsCodeCalculation
} from '../../../types/patientmr/oasis';
import {
  getFieldsByCategory,
  getFieldsForAssessmentType,
  validateFieldDependencies
} from '../../../config/oasis-fields';
import { OasisCategories } from '../../../types/patientmr/oasis';

interface OasisFormProps {
  formData: OasisFormData;
  patientId: string;
  onSave: (data: OasisFormData) => void;
  onSubmit?: () => void;
  onReopen?: () => void;
  onApprove?: () => void;
  onCancel: () => void;
  saving: boolean;
  submitting?: boolean;
  reopening?: boolean;
  isEditing: boolean;
  oasisStatus?: AssessmentStatus; // Changed from OasisStatus to AssessmentStatus
  canReadAssessment?: boolean;
  canCreateAssessment?: boolean;
  canUpdateAssessment?: boolean;
}

const OasisForm: React.FC<OasisFormProps> = ({ 
  formData, 
  patientId,
  onSave, 
  onSubmit,
  onReopen,
  onApprove,
  onCancel, 
  saving,
  submitting = false,
  reopening = false, 
  isEditing,
  oasisStatus,
  canReadAssessment: propCanReadAssessment,
  canCreateAssessment: propCanCreateAssessment,
  canUpdateAssessment: propCanUpdateAssessment
}) => {
  // Permission checks for OASIS assessments - use props if provided, otherwise check locally
  const localCanUpdateAssessment = useHasAnyPermission([
    'patientmr:phi:assessment:update' as any,
    'patientmr:phi:assessment:updateall' as any
  ]);

  // Use props if provided, otherwise use local permission checks
  const canUpdateAssessment = propCanUpdateAssessment !== undefined ? propCanUpdateAssessment : localCanUpdateAssessment;

  // Read-only when user cannot update
  const isReadOnlyView = !canUpdateAssessment;
  const [responses, setResponses] = useState<OasisResponses>(formData.responses);
  const [assessmentDate, setAssessmentDate] = useState(formData.assessmentDate);
  const [episodeStartDate, setEpisodeStartDate] = useState(formData.episodeStartDate || '');
  const [episodeEndDate, setEpisodeEndDate] = useState(formData.episodeEndDate || '');
  const [validation, setValidation] = useState<OasisValidationResult | null>(null);
  const [showHippsConfirmation, setShowHippsConfirmation] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<OasisFormData | null>(null);
  const [newCalculatedHipps, setNewCalculatedHipps] = useState<HippsCodeCalculation | null>(null);
  
  
  // Add ref to track if form has been modified by user
  const userHasModifiedForm = useRef(false);
  const initialFormDataRef = useRef(JSON.stringify(formData));

  // Get status color for badge
  const getStatusColor = (status?: AssessmentStatus): string => {
    if (!status) return 'bg-gray-50 text-gray-700 border border-gray-200';
    
    switch (status) {
      case AssessmentStatus.DRAFT:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
      case AssessmentStatus.SUBMITTED:
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case AssessmentStatus.QA:
        return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
      case AssessmentStatus.COMPLETE:
        return 'bg-green-50 text-green-700 border border-green-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };
  
  // Initialize hippsCalculation with existing HIPPS code if available
  const initialHippsCalculation = (formData as any).hippsCode ? {
    hippsCode: (formData as any).hippsCode,
    points: 0,
    factors: { clinical: 0, functional: 0, service: 0 },
    description: 'HIPPS code from existing assessment'
  } : null;
  
  const [hippsCalculation, setHippsCalculation] = useState<HippsCodeCalculation | null>(initialHippsCalculation);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    [OasisCategories.ADMINISTRATIVE]: true,
    [OasisCategories.DIAGNOSES]: true,
    [OasisCategories.SENSORY_STATUS]: false,
    [OasisCategories.ADL_IADL]: false,
    [OasisCategories.MEDICATIONS]: false,
    [OasisCategories.THERAPY_PLAN]: false,
    [OasisCategories.DISCHARGE]: false
  });

  // Get relevant fields for this assessment type
  const relevantFields = getFieldsForAssessmentType(formData.type);
  const categories = Object.values(OasisCategories).filter(category => 
    relevantFields.some(field => field.category === category)
  );

  // Update responses when form data changes - PREVENT FORM RESET during HIPPS calculation
  useEffect(() => {
    // Only update form data if this is a genuinely new formData object (not a HIPPS calculation update)
    const newFormDataStr = JSON.stringify(formData);
    
    // Check if this is the initial load or if user hasn't modified the form yet
    if (!userHasModifiedForm.current || initialFormDataRef.current !== newFormDataStr) {
      // Only update if the core form data has actually changed (not just HIPPS calculation)
      const coreDataChanged = (
        formData.responses !== responses ||
        formData.assessmentDate !== assessmentDate ||
        formData.episodeStartDate !== episodeStartDate ||
        formData.episodeEndDate !== episodeEndDate
      );
      
      if (coreDataChanged) {
        setResponses(formData.responses);
        setAssessmentDate(formData.assessmentDate);
        setEpisodeStartDate(formData.episodeStartDate || '');
        setEpisodeEndDate(formData.episodeEndDate || '');
        
        // Update the initial form data reference
        initialFormDataRef.current = newFormDataStr;
      }
    }
    
    // Initialize HIPPS code from backend data if available and not already set
    const existingHippsCode = (formData as any).hippsCode;
    if (existingHippsCode && !hippsCalculation) {
      // Create a minimal hippsCalculation object with the HIPPS code
      setHippsCalculation({
        hippsCode: existingHippsCode,
        points: 0, // We don't know the points, so use 0 as a placeholder
        factors: {
          clinical: 0,
          functional: 0,
          service: 0
        },
        description: 'HIPPS code from existing assessment'
      });
    }
  }, [formData, responses, assessmentDate, episodeStartDate, episodeEndDate, hippsCalculation]);

  // Validate form when responses change
  useEffect(() => {
    const currentFormData: OasisFormData = {
      ...formData,
      assessmentDate,
      episodeStartDate: episodeStartDate || undefined,
      episodeEndDate: episodeEndDate || undefined,
      responses
    };
    
    try {
      const validationResult = OasisService.validateOasisData(currentFormData);
      console.log('OasisForm validation result:', validationResult);
      setValidation(validationResult);
    } catch (error) {
      console.error('Error during OASIS validation:', error);
      // Set a default validation result to avoid blocking the form
      setValidation({
        isValid: true,
        errors: [],
        warnings: []
      });
    }
  }, [responses, assessmentDate, episodeStartDate, episodeEndDate, formData]);

  // Handle field value change - TRACK user modifications
  const handleFieldChange = (fieldKey: string, value: string | string[]) => {
    // Mark that user has modified the form
    userHasModifiedForm.current = true;
    
    setResponses(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Calculate HIPPS code - PRESERVE form state during calculation
  const handleCalculateHipps = () => {
    try {
      // Mark that user has initiated a calculation (not an external update)
      userHasModifiedForm.current = true;
      
      // Calculate HIPPS code based on current responses without modifying the form
      const calculation = OasisService.calculateSimpleHippsCode(responses);
      
      // Only update the HIPPS calculation data without affecting form fields
      setHippsCalculation(calculation);
      
      console.log("HIPPS calculation successful:", calculation);
    } catch (error) {
      console.error("Error calculating HIPPS code:", error);
    }
  };

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Allow form submission even if validation is incomplete or has issues
    // We'll show warnings instead of blocking the save
    const submitData: OasisFormData = {
      ...formData,
      assessmentDate,
      episodeStartDate: episodeStartDate || undefined,
      episodeEndDate: episodeEndDate || undefined,
      responses
    };

    // For update operations (editing), automatically recalculate the HIPPS code
    if (isEditing) {
      try {
        // Calculate a new HIPPS code based on current responses
        const freshCalculation = OasisService.calculateSimpleHippsCode(responses);
        
        // If we have an existing HIPPS code and it's different from the newly calculated one
        if (hippsCalculation && freshCalculation.hippsCode !== hippsCalculation.hippsCode) {
          // Store the data for later submission after confirmation
          setPendingSubmitData(submitData);
          // Store the newly calculated HIPPS for the confirmation dialog
          setNewCalculatedHipps(freshCalculation);
          // Show the confirmation dialog
          setShowHippsConfirmation(true);
          return; // Stop here and wait for user confirmation
        } else {
          // If no previous code exists or the code is unchanged, just use the new calculation
          (submitData as any).hippsCode = freshCalculation.hippsCode;
          setHippsCalculation(freshCalculation);
        }
      } catch (err) {
        console.error('Error auto-calculating HIPPS code:', err);
        // Continue with existing HIPPS code if calculation fails
        if (hippsCalculation) {
          (submitData as any).hippsCode = hippsCalculation.hippsCode;
        }
      }
    } else {
      // For new assessments, use the existing logic
      let hippsCodeToSubmit = '';
      if (hippsCalculation) {
        hippsCodeToSubmit = hippsCalculation.hippsCode;
      } else {
        // Try to calculate HIPPS code automatically before submission
        try {
          const calculation = OasisService.calculateSimpleHippsCode(responses);
          hippsCodeToSubmit = calculation.hippsCode;
          // Update the UI with the calculation
          setHippsCalculation(calculation);
        } catch (err) {
          console.error('Error auto-calculating HIPPS code:', err);
          // Proceed without a HIPPS code if calculation fails
        }
      }

      // Include the HIPPS code in the submission data
      if (hippsCodeToSubmit) {
        (submitData as any).hippsCode = hippsCodeToSubmit;
      }
    }

    // Submit if we didn't show the confirmation dialog
    onSave(submitData);
  };
  
  // Handle HIPPS code confirmation
  const handleConfirmHipps = (confirmed: boolean) => {
    if (!pendingSubmitData || !newCalculatedHipps) return;
    
    // Create a copy of the pending data
    const dataToSubmit = { ...pendingSubmitData };
    
    if (confirmed) {
      // Use the newly calculated HIPPS code if confirmed
      (dataToSubmit as any).hippsCode = newCalculatedHipps.hippsCode;
      // Update the UI to show the new calculation
      setHippsCalculation(newCalculatedHipps);
    } else {
      // Keep the existing HIPPS code if not confirmed
      if (hippsCalculation) {
        (dataToSubmit as any).hippsCode = hippsCalculation.hippsCode;
      }
    }
    
    // Close the dialog and submit the data
    setShowHippsConfirmation(false);
    setPendingSubmitData(null);
    setNewCalculatedHipps(null);
    onSave(dataToSubmit);
  };

  // Render field based on type
  const renderField = (field: any) => {
    const fieldKey = field.key;
    const value = responses[fieldKey as keyof OasisResponses] || '';
    const isRequired = field.required && validateFieldDependencies(responses, field);
    const hasError = validation?.errors.some(error => error.field === fieldKey);

    // Skip field if dependencies aren't met
    if (!validateFieldDependencies(responses, field)) {
      return null;
    }

    const fieldId = `oasis-${fieldKey}`;

    switch (field.type) {
      case 'text':
      case 'textarea':
        return (
          <div key={fieldKey} className="space-y-2">
            <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 text-left">
              {field.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
              {field.oasisItem && (
                <span className="text-gray-500 ml-2 text-xs">({field.oasisItem})</span>
              )}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                id={fieldId}
                value={value as string}
                onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                disabled={isReadOnlyView}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  hasError ? 'border-red-300' : 'border-gray-300'
                } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                rows={3}
                placeholder={field.helpText}
              />
            ) : (
              <input
                id={fieldId}
                type="text"
                value={value as string}
                onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                disabled={isReadOnlyView}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  hasError ? 'border-red-300' : 'border-gray-300'
                } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                placeholder={field.helpText}
              />
            )}
            {field.helpText && (
              <p className="text-sm text-gray-500">{field.helpText}</p>
            )}
          </div>
        );

      case 'number':
        return (
          <div key={fieldKey} className="space-y-2">
            <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 text-left">
              {field.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
              {field.oasisItem && (
                <span className="text-gray-500 ml-2 text-xs">({field.oasisItem})</span>
              )}
            </label>
            <input
              id={fieldId}
              type="number"
              value={value as string}
              onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
              disabled={isReadOnlyView}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                hasError ? 'border-red-300' : 'border-gray-300'
              } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
              min={field.validation?.min}
              max={field.validation?.max}
            />
            {field.helpText && (
              <p className="text-sm text-gray-500">{field.helpText}</p>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={fieldKey} className="space-y-2">
            <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 text-left">
              {field.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
              {field.oasisItem && (
                <span className="text-gray-500 ml-2 text-xs">({field.oasisItem})</span>
              )}
            </label>
            <input
              id={fieldId}
              type="date"
              value={value as string}
              onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
              disabled={isReadOnlyView}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                hasError ? 'border-red-300' : 'border-gray-300'
              } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
            />
            {field.helpText && (
              <p className="text-sm text-gray-500">{field.helpText}</p>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={fieldKey} className="space-y-2">
            <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 text-left">
              {field.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
              {field.oasisItem && (
                <span className="text-gray-500 ml-2 text-xs">({field.oasisItem})</span>
              )}
            </label>
            <select
              id={fieldId}
              value={value as string}
              onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
              disabled={isReadOnlyView}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                hasError ? 'border-red-300' : 'border-gray-300'
              } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
            >
              <option value="">Select...</option>
              {field.options?.map((option: any) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {field.helpText && (
              <p className="text-sm text-gray-500">{field.helpText}</p>
            )}
          </div>
        );

      case 'multiselect':
        const multiValue = Array.isArray(value) ? value : [];
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 text-left">
              {field.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
              {field.oasisItem && (
                <span className="text-gray-500 ml-2 text-xs">({field.oasisItem})</span>
              )}
            </label>
            <div className={`space-y-2 ${isReadOnlyView ? 'opacity-60' : ''}`}>
              {field.options?.map((option: any) => (
                <label key={option.value} className={`flex items-center ${isReadOnlyView ? 'cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={multiValue.includes(option.value)}
                    onChange={(e) => {
                      const newValue = e.target.checked
                        ? [...multiValue, option.value]
                        : multiValue.filter((v: string) => v !== option.value);
                      handleFieldChange(fieldKey, newValue);
                    }}
                    disabled={isReadOnlyView}
                    className={`mr-2 ${isReadOnlyView ? 'cursor-not-allowed' : ''}`}
                  />
                  <span className={`text-sm ${isReadOnlyView ? 'text-gray-500' : ''}`}>{option.label}</span>
                </label>
              ))}
            </div>
            {field.helpText && (
              <p className="text-sm text-gray-500">{field.helpText}</p>
            )}
          </div>
        );

      case 'icd-select':
        return (
          <div key={fieldKey} className="space-y-2">
            <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 text-left">
              {field.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
              {field.oasisItem && (
                <span className="text-gray-500 ml-2 text-xs">({field.oasisItem})</span>
              )}
            </label>
            <ICDSelect
              id={fieldId}
              value={(value as string) || ''}
              onChange={(next) => handleFieldChange(fieldKey, next)}
              disabled={isReadOnlyView}
              invalid={!!hasError}
            />
            {field.helpText && (
              <p className="text-sm text-gray-500">{field.helpText}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };
  
  // Helper function to get display name for Assessment status
  const getStatusDisplayName = (status?: AssessmentStatus): string => {
    if (!status) return 'Draft';
    
    switch (status) {
      case AssessmentStatus.DRAFT:
        return 'Draft';
      case AssessmentStatus.SUBMITTED:
        return 'Submitted';
      case AssessmentStatus.QA:
        return 'QA';
      case AssessmentStatus.COMPLETE:
        return 'Complete';
      case AssessmentStatus.CURRENT:
        return 'Current';
      case AssessmentStatus.EXPIRED:
        return 'Expired';
      default:
        return 'Unknown Status';
    }
  };

  return (
    <div className="space-y-6">
      {/* Form Header */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Edit' : 'New'} {OasisService.getOasisTypeDisplayName(formData.type)} Assessment
          </h3>
          {/* Status Badge - Only show when editing */}
          {isEditing && oasisStatus && (
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(oasisStatus)}`}>
                {getStatusDisplayName(oasisStatus)}
              </span>
            </div>
          )}
        </div>
        <p className="text-sm text-gray-600">
          Complete the required OASIS items below. Required fields are marked with an asterisk (*).
        </p>
      </div>

      {/* Read-only mode notification */}
      {isReadOnlyView && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
          <p className="text-sm text-gray-700">
            <strong>View Only:</strong> You do not have permission to edit OASIS Assessments. Viewing in read-only mode.
          </p>
        </div>
      )}

      {/* Top Form Actions */}
      <FormButtonBar
        onCancel={onCancel}
        onSave={!isReadOnlyView ? () => {
          const submitData: OasisFormData = {
            ...formData,
            assessmentDate,
            episodeStartDate: episodeStartDate || undefined,
            episodeEndDate: episodeEndDate || undefined,
            responses
          };
          if (hippsCalculation) {
            (submitData as any).hippsCode = hippsCalculation.hippsCode;
          }
          onSave(submitData);
        } : undefined}
        onSubmit={!isReadOnlyView ? onSubmit : undefined}
        onReopen={!isReadOnlyView ? onReopen : undefined}
        onApprove={!isReadOnlyView ? onApprove : undefined}
        saving={saving}
        submitting={submitting}
        reopening={reopening}
        isEditing={isEditing}
        assessmentStatus={oasisStatus}
        disabled={isReadOnlyView}
        className="mb-6"
      />

      {/* Form */}
      <form id="oasis-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900">Assessment Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="assessmentDate" className="block text-sm font-medium text-gray-700 text-left">
                Assessment Date <span className="text-red-500">*</span>
              </label>
              <input
                id="assessmentDate"
                type="date"
                value={assessmentDate}
                onChange={(e) => setAssessmentDate(e.target.value)}
                disabled={isReadOnlyView}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                required
              />
            </div>
            {formData.type === OasisType.OASIS_SOC && (
              <div>
                <label htmlFor="episodeStartDate" className="block text-sm font-medium text-gray-700 text-left">
                  Episode Start Date
                </label>
                <input
                  id="episodeStartDate"
                  type="date"
                  value={episodeStartDate}
                  onChange={(e) => setEpisodeStartDate(e.target.value)}
                  disabled={isReadOnlyView}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                />
              </div>
            )}
            {formData.type === OasisType.OASIS_DISCHARGE && (
              <div>
                <label htmlFor="episodeEndDate" className="block text-sm font-medium text-gray-700 text-left">
                  Episode End Date
                </label>
                <input
                  id="episodeEndDate"
                  type="date"
                  value={episodeEndDate}
                  onChange={(e) => setEpisodeEndDate(e.target.value)}
                  disabled={isReadOnlyView}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                />
              </div>
            )}
          </div>
          
          {/* Discharge-specific information banner */}
          {formData.type === OasisType.OASIS_DISCHARGE && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Info size={20} className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-1">Discharge Assessment</h4>
                  <p className="text-sm text-blue-700">
                    This assessment captures the patient's status at discharge and is used for quality reporting.
                    Complete all discharge-specific fields including functional status comparisons and emergency department usage.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* OASIS Fields by Category */}
        {categories.map(category => {
          const categoryFields = getFieldsByCategory(category).filter(field => 
            relevantFields.some(rf => rf.key === field.key)
          );
          
          if (categoryFields.length === 0) return null;

          return (
            <div key={category} className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 flex items-center"
              >
                {expandedCategories[category] ? (
                  <ChevronDown size={20} className="text-gray-400 mr-2" />
                ) : (
                  <ChevronRight size={20} className="text-gray-400 mr-2" />
                )}
                <h4 className="text-md font-medium text-gray-900">{category}</h4>
              </button>
              
              {expandedCategories[category] && (
                <div className="p-4 space-y-4">
                  {categoryFields.map(field => renderField(field))}
                </div>
              )}
            </div>
          );
        })}

        {/* HIPPS Code Calculation */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-semibold text-blue-900">HIPPS Code</h4>
            <PrimaryButtonLight
              type="button"
              onClick={handleCalculateHipps}
              disabled={isReadOnlyView}
              className={`text-blue-700 hover:text-blue-800 px-3 py-1 text-sm ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center">
              <Calculator size={16} className="mr-2" />
              <span>Calculate</span>
              </div>
            </PrimaryButtonLight>
          </div>
          {hippsCalculation && (
            <div className="bg-white rounded border p-4 flex flex-col space-y-2">
              <div>
                <span className="block text-lg font-bold text-blue-700 tracking-wider">{hippsCalculation.hippsCode}</span>
              </div>
              <div className="flex flex-wrap gap-4 text-base text-gray-800">
                <span><span className="font-medium">Points:</span> {hippsCalculation.points}</span>
                <span><span className="font-medium">Clinical:</span> {hippsCalculation.factors.clinical}</span>
                <span><span className="font-medium">Functional:</span> {hippsCalculation.factors.functional}</span>
                <span><span className="font-medium">Service:</span> {hippsCalculation.factors.service}</span>
              </div>
              <div className="text-sm text-gray-500 pt-1 border-t mt-2">{hippsCalculation.description}</div>
            </div>
          )}
        </div>

        {/* Validation Messages */}
        {validation && (validation.errors.length > 0 || validation.warnings.length > 0) && (
          <div className="space-y-2">
            {validation.errors.map((error, index) => (
              <div key={index} className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center">
                <AlertCircle size={16} className="mr-2 flex-shrink-0" />
                <span className="text-sm">{error.message}</span>
              </div>
            ))}
            {validation.warnings.map((warning, index) => (
              <div key={index} className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded flex items-center">
                <Info size={16} className="mr-2 flex-shrink-0" />
                <span className="text-sm">{warning.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Form Actions (Bottom) */}
        <FormButtonBar
          onCancel={onCancel}
          onSave={!isReadOnlyView ? () => {
            const submitData: OasisFormData = {
              ...formData,
              assessmentDate,
              episodeStartDate: episodeStartDate || undefined,
              episodeEndDate: episodeEndDate || undefined,
              responses
            };
            if (hippsCalculation) {
              (submitData as any).hippsCode = hippsCalculation.hippsCode;
            }
            onSave(submitData);
          } : undefined}
          onSubmit={!isReadOnlyView ? onSubmit : undefined}
          onReopen={!isReadOnlyView ? onReopen : undefined}
          onApprove={!isReadOnlyView ? onApprove : undefined}
          saving={saving}
          submitting={submitting}
          reopening={reopening}
          isEditing={isEditing}
          assessmentStatus={oasisStatus}
          disabled={isReadOnlyView}
          className="pt-4 border-t"
        />
      </form>
      
      {/* HIPPS Code Confirmation Dialog */}
      {showHippsConfirmation && newCalculatedHipps && (
        <Dialog
          open={showHippsConfirmation}
          onClose={() => handleConfirmHipps(false)}
        >
          <div className="py-4 space-y-4">
            <div className="flex items-center mb-3">
              <AlertTriangle size={24} className="text-amber-500 mr-3" />
              <h3 className="text-xl font-semibold">HIPPS Code Updated</h3>
            </div>
            <p className="text-gray-700">
              The HIPPS code has changed based on your assessment updates
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex flex-col items-start">
                <span className="font-medium">Previous HIPPS Code:</span>
                <span className="font-mono font-bold text-gray-700 mt-1">
                  {hippsCalculation?.hippsCode || 'None'}
                </span>
              </div>
              
              <div className="flex flex-col items-start mt-3">
                <span className="font-medium">New HIPPS Code:</span>
                <span className="font-mono font-bold text-blue-600 mt-1">
                  {newCalculatedHipps.hippsCode}
                </span>
              </div>
              
              <div className="text-sm text-gray-600 space-y-1 mt-2 border-t pt-2">
                <p>Total Points: {newCalculatedHipps.points}</p>
                <p>Clinical: {newCalculatedHipps.factors.clinical}, Functional: {newCalculatedHipps.factors.functional}, Service: {newCalculatedHipps.factors.service}</p>
                <p className="text-xs italic">{newCalculatedHipps.description}</p>
              </div>
            </div>
            
            <p className="text-gray-600">
              Would you like to use this newly calculated HIPPS code for this assessment?
            </p>
            
            <div className="flex space-x-3 pt-2">
              <PrimaryButtonLight onClick={() => handleConfirmHipps(false)}>
                Keep Original Code
              </PrimaryButtonLight>
              <PrimaryButton 
                onClick={() => handleConfirmHipps(true)}
                className="bg-slate-800 hover:bg-slate-700 text-white"
              >
                <div className="flex items-center">
                  <Check size={16} className="mr-2" />
                  <span>Use New Code</span>
                </div>
              </PrimaryButton>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default OasisForm;
